import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaPlay, FaHeart, FaInfoCircle } from 'react-icons/fa';
import { Swiper, SwiperSlide } from 'swiper/react';
import { encryptId } from '../SecurityComponent/cryptoUtils';
import { useDispatch } from 'react-redux';
import { pushFavourite, removeFavourite, check } from '../../Redux/actions/FavouriteThunk';
import { message } from 'antd';
import './spotlight.scss';

const AUTO_DELAY = 6000; // ms — thời gian giữa mỗi lần tự chuyển phim

const SpotlightList = ({ genreId }) => {
    const [movies, setMovies] = useState([]);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [loading, setLoading] = useState(true);
    const [liked, setLiked] = useState(false);
    const [paused, setPaused] = useState(false);
    const dispatch = useDispatch();
    const autoTimer = useRef(null);

    useEffect(() => {
        setLoading(true);
        const url = genreId
            ? `/movies-public-api.php?type=byGenre&genreId=${genreId}&page=0&size=20`
            : `/movies-public-api.php?type=popular&page=0&size=20`;
        fetch(url)
            .then(r => r.json())
            .then(d => { if (d.success) setMovies(d.content || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [genreId]);

    // Tự chuyển phim mỗi AUTO_DELAY ms — dừng khi rê chuột vào, tiếp tục khi rê ra.
    useEffect(() => {
        if (!movies.length || paused) return;
        autoTimer.current = setInterval(() => {
            setSelectedIdx(i => (i + 1) % movies.length);
        }, AUTO_DELAY);
        return () => clearInterval(autoTimer.current);
    }, [movies.length, paused]);

    useEffect(() => {
        if (!movies.length) return;
        setLiked(false);
        const featured = movies[selectedIdx];
        if (!featured?.id || !localStorage.getItem('accessToken')) return;
        dispatch(check(featured.id)).then(d => setLiked(!!d)).catch(() => {});
    }, [selectedIdx, movies, dispatch]);

    const handleLike = async (e) => {
        e.preventDefault();
        const featured = movies[selectedIdx];
        if (!featured?.id) return;
        try {
            if (!liked) {
                await dispatch(pushFavourite(featured.id));
                message.success('Đã thêm vào yêu thích!');
                setLiked(true);
            } else {
                await dispatch(removeFavourite(featured.id));
                message.success('Đã xóa khỏi yêu thích!');
                setLiked(false);
            }
        } catch { message.error('Lỗi!'); }
    };

    if (loading) return <div className="sl-loading">Loading...</div>;
    if (!movies.length) return null;

    const featured = movies[selectedIdx];
    const watchUrl = `/watch/${featured.slug}/${encryptId(1)}`;
    const backdrop = featured.thumbnailUrl || featured.posterUrl;
    const desc = featured.description?.replace(/<[^>]+>/g, '').trim() || '';

    return (
        <div className="spotlight" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            {/* ── Hero ── */}
            <div className="spotlight__hero" style={{ backgroundImage: `url(${backdrop})` }}>
                <div className="spotlight__overlay" />
                <div className="spotlight__info">
                    <h2 className="spotlight__title">{featured.title}</h2>
                    <div className="spotlight__badges">
                        {featured.quality && <span className="spotlight__badge spotlight__badge--quality">{featured.quality}</span>}
                        {featured.releaseYear && <span className="spotlight__badge">{featured.releaseYear}</span>}
                        {featured.episodeCount > 1 && (
                            <span className="spotlight__badge">Tập {featured.latestEpisode || featured.episodeCount}</span>
                        )}
                    </div>
                    <div className="spotlight__chips">
                        {featured.genres?.slice(0, 3).map(g => (
                            <span key={g.id} className="spotlight__chip">{g.name}</span>
                        ))}
                    </div>
                    {desc && <p className="spotlight__desc">{desc}</p>}
                    <div className="spotlight__actions">
                        <Link to={watchUrl} className="spotlight__play" title="Xem ngay">
                            <FaPlay size={16} />
                        </Link>
                        <button className={`spotlight__icon${liked ? ' liked' : ''}`} onClick={handleLike} title="Yêu thích">
                            <FaHeart size={14} />
                        </button>
                        <Link to={`/detail/${featured.slug}`} className="spotlight__icon" title="Chi tiết">
                            <FaInfoCircle size={14} />
                        </Link>
                    </div>
                </div>
            </div>

            {/* ── Thumbnail Strip (Swiper — kéo chuột được) ── */}
            <div className="spotlight__strip">
                <Swiper
                    grabCursor={true}
                    spaceBetween={8}
                    slidesPerView="auto"
                    preventClicks={false}
                    preventClicksPropagation={false}
                >
                    {movies.map((m, i) => (
                        <SwiperSlide key={m.id} style={{ width: 108 }}>
                            <div
                                className={`spotlight__thumb${i === selectedIdx ? ' active' : ''}`}
                                style={{ backgroundImage: `url(${m.thumbnailUrl || m.posterUrl})` }}
                                onClick={() => setSelectedIdx(i)}
                                title={m.title}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    );
};

export default SpotlightList;
